<script lang="ts">
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Design system components
	import {
		Button,
		Checkbox,
		Input,
		Text,
		Container,
		Stack,
		Toggle,
		toast,
	} from '@Shelly/shelly-design-system';
	//Hooks
	import { onMount } from 'svelte';
	//Services
	import { format } from 'svelte-i18n';
	import { localReq, postReq } from '../../../../services/http';
	//Constants
	import { DEVICE_MAX_POWER_DEFAULTS } from '../../../../constants';
	//Helpers
	import {
		capFirstChar,
		number_to_letter,
		makeNumbersPrimaryAndBold,
		getErrorOrInfoMessageForMinAndMax,
	} from '../../../../helpers/utilities';
	import { get_local_ip, real_id } from '../../../../helpers/interface_helpers';
	//Stores
		import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { restrictDecimalNumberInput } from 'src/helpers/number_input_helpers';

	$: settingsStore = getDeviceSettings(id);
	export let device;

	const id = real_id(device.id);

	let settings = $settingsStore;

	const min = 0;

	const max = DEVICE_MAX_POWER_DEFAULTS[device.type];

	let local_ip = get_local_ip(id);
	const translationsWithWatts = true;

	let values = {},
		checked_ = {},
		loading = {};
	let cache = [];
	let cacheChecked = [];
	let lastCustomValues = {};

	onMount(() => {
		for (let i = 0; i < 3; i++) {
			values[i] = $settingsStore[device.category + 's'][i].max_power;
			checked_[i] = values[i] > 0;
			loading[i] = false;
			cache.splice(i, 0, values[i]);
			cacheChecked.splice(i, 0, checked_[i]);
			lastCustomValues[i] = values[i] > 0 ? values[i] : max;
		}
	});

	const savePower = async (i) => {
		if (cache[i] !== values[i] || cacheChecked[i] !== checked_[i]) {
			let res;
			loading[i] = true;
			if (local_ip) {
				res = await localReq(device, `${local_ip}/settings`, 'post', {
					max_power: values[i],
				});
			} else {
				res = await postReq(
					`/device/${device.category !== 'emeter' ? device.category : 'relay'}/settings/power_max`,
					{
						value: values[i],
						channel: i,
						id: id,
					},
				);
			}

			if (res.isok) {
				settings[device.category + 's'][i].max_power = values[i];
				devices_settings.update_devices_settings(id, settings);
				checked_[i] = +values[i] > 0;
				if (+values[i] > 0) {
					lastCustomValues[i] = values[i];
				}
				if (values[i] === 0) {
					toast.open({
						type: 'success',
						title:
							`${$format('power_protection_disable_custom', {
								values: { phase: i + 1 },
							})}` || `${capFirstChar(device.type)} custom power protection was disabled.`,
					});
				} else {
					toast.open({
						type: 'success',
						title:
							`${$format('power_protection_save_scc_toast', {
								values: { value: values[i], phase: i + 1 },
							})}` ||
							`${capFirstChar(device.type)} custom power protection was set to ${values}W.`,
					});
				}
				cache.splice(i, 1, values[i]);
				cacheChecked.splice(i, 1, checked_[i]);
			} else {
				values[i] = cache[i];
				checked_[i] = cacheChecked[i];
				toast.open({
					type: 'danger',
					title: $format('power_protection_save_err_toast'),
				});
			}
			loading[i] = false;
		}
	};
	const handleToggler = async (i) => {
		if (loading[i]) return;

		if (checked_[i]) {
			if (+values[i] > 0) {
				lastCustomValues[i] = values[i];
			}
			checked_[i] = false;
			values[i] = 0;
		} else {
			checked_[i] = true;
			values[i] = +lastCustomValues[i] > 0 ? lastCustomValues[i] : max;
		}

		if ($showNewLayout) {
			await savePower(i);
		}
	};
</script>

<Stack gap={$showNewLayout ? 0 : 's'}>
	{#each Object.values(values) as value, i}
		{#if $showNewLayout && i > 0}
			<hr class="mt-l mb-l" />
		{/if}
		{#if !$showNewLayout}
			<Container type="modalContainer" class="border-color-transparent" padding="xs">
				<Text element="h2" class="mb-s">
					{$format(`label_phase_${number_to_letter(i, true).toLowerCase()}`)}
				</Text>
				<Checkbox
					size="m"
					class="mb-s"
					bind:checked={checked_[i]}
					on:click={() => handleToggler(i)}
					id="checkbox-9ffcv3x"
				>
					{$format('custom_max_power_protection')}
				</Checkbox>
				{#if checked_[i]}
					<Text styleType="controls-small" class="mb-s">
						{@html makeNumbersPrimaryAndBold(
							`${$format('device_turn_off_label', {
								values: { value: values[i] },
								default: `Device will turn off when power reaches ${value} W.`,
							})}`,
						)}
					</Text>
					<Input
						aria-label={$format('input_power_protection_max_power', {
							default: 'Input Power protection max power',
						})}
						on:input={(e) => restrictDecimalNumberInput(e, 2)}
						class="mb-s"
						size="m"
						bind:value={values[i]}
						type={'number'}
						{min}
						{max}
						info={getErrorOrInfoMessageForMinAndMax(
							values[i],
							min,
							max,
							translationsWithWatts,
						).info}
						error={getErrorOrInfoMessageForMinAndMax(
							values[i],
							min,
							max,
							translationsWithWatts,
						).error}
						disabled={!checked_[i]}
						step={1}
						on:enter={() => savePower(i)}
						id="input-ewmgzav"
					/>
					<Button
						loading={loading[i]}
						disabled={loading[i]}
						width="full"
						size="m"
						on:click={() => savePower(i)}>{$format('button_save')}</Button
					>
				{/if}
			</Container>
		{:else}
			<Text element="h2" class="mb-xs">
				{$format(`label_phase_${number_to_letter(i, true).toLowerCase()}`)}
			</Text>
			<Toggle
				size="m"
				class="mt-s-plus mb-s-plus"
				checked={checked_[i]}
				disabled={loading[i]}
				on:click={() => handleToggler(i)}
				id="toggle-vf2v2qg"
			>
				{$format('custom_max_power_protection', {
					default: 'Custom max power protection',
				})}
			</Toggle>
			{#if checked_[i]}
				<Text styleType="controls-small" class="mt-s-plus mb-s">
					{@html makeNumbersPrimaryAndBold(
						`${$format('turn_device_off_when_power_reaches', {
							values: { value: values[i] },
							default: `Turn the device off when power reaches ${values[i]} W.`,
						})}`,
					)}
				</Text>
				<Input
					aria-label={$format('input_power_protection_max_power', {
						default: 'Input Power protection max power',
					})}
					on:input={(e) => restrictDecimalNumberInput(e, 2)}
					class="mb-s"
					size="m"
					bind:value={values[i]}
					type={'number'}
					{min}
					{max}
					info={getErrorOrInfoMessageForMinAndMax(values[i], min, max, translationsWithWatts)
						.info}
					error={getErrorOrInfoMessageForMinAndMax(values[i], min, max, translationsWithWatts)
						.error}
					disabled={!checked_[i]}
					step={1}
					on:enter={() => savePower(i)}
					id="input-q74crv5"
				/>
				<Button
					loading={loading[i]}
					disabled={loading[i]}
					width="full"
					size="m"
					on:click={() => savePower(i)}>{$format('button_save')}</Button
				>
			{/if}
		{/if}
	{/each}
</Stack>
